import { ContentItemIcon } from 'ContentItemIcon';
import { useExpandableContainer } from 'ExpandableContainer';
import { type KeyboardEvent, type FC, useState, useMemo, useCallback, type JSX, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { useOfflineModeManager } from 'FrontRoyalAngular';
import { ContentItemBoxExpandableContainer } from './ContentItemBoxExpandableContainer';
import { ContentItemBoxEndIcon } from './ContentItemBoxEndIcon';
import { ContentItemBoxText } from './ContentItemBoxText';
import {
    type ContentItemBoxProps,
    type OnPressOrExpandedContent,
    type WithExpandedContent,
    type WithOnPress,
} from './ContentItemBox.types';

const hasOnPress = (p: OnPressOrExpandedContent): p is WithOnPress => 'onPress' in p;

const hasExpandedContent = (p: OnPressOrExpandedContent): p is WithExpandedContent => 'expandedContent' in p;

const getHeightClass = (height: ContentItemBoxProps['height'], desktopHeight: ContentItemBoxProps['desktopHeight']) => {
    let heightClass = '';
    if (height === 12) heightClass = 'min-h-12';
    if (height === 14.5) heightClass = 'min-h-14.5';
    if (height === 16.5) heightClass = 'min-h-16.5';
    if (height === 18) heightClass = 'min-h-18';
    let desktopHeightClass = '';

    if (desktopHeight) {
        if (desktopHeight === 12) desktopHeightClass = 'md:min-h-12';
        if (desktopHeight === 14.5) desktopHeightClass = 'md:min-h-14.5';
        if (desktopHeight === 16.5) desktopHeightClass = 'md:min-h-16.5';
        if (desktopHeight === 18) desktopHeightClass = 'md:min-h-18';
    }

    return twMerge(heightClass, desktopHeightClass);
};

const getClassName = ({
    endIcon,
    onPress,
    addKeydownClass,
    fullWidth,
    locked,
    identifierClassName,
}: {
    endIcon: JSX.Element | undefined;
    onPress: boolean | (() => void);
    addKeydownClass: boolean;
    fullWidth: boolean;
    locked: boolean;
    identifierClassName?: string;
}) =>
    twMerge(
        identifierClassName,
        'flex flex-col rounded-8 md:rounded-5 bg-white cursor-pointer overflow-hidden',
        'border border-solid border-slate-grey-200 hover:border-slate-grey-300',
        'group',
        'hover:shadow-small',
        'transition-all duration-200',
        endIcon ? 'justify-between' : 'justify-start',
        fullWidth ? 'w-full' : 'w-fit',
        onPress && !locked && 'active:scale-[.99]',
        onPress && !locked && addKeydownClass && 'scale-[.99]',
        locked && 'opacity-80 cursor-default pointer-events-none',
    );

function getPaddingClass(padding: ContentItemBoxProps['padding']) {
    return (
        {
            2.5: 'p-2.5',
            3.5: 'p-3.5',
        }[padding ?? '3.5'] ?? 'p-3.5'
    );
}

export const ContentItemBox: FC<ContentItemBoxProps> = ({
    src,
    title,
    imageSize,
    desktopImageSize,
    titleSize = 'base',
    endIcon,
    height = 14.5,
    desktopHeight,
    fullWidth = false,
    availableOffline = false,
    subtitle,
    padding = '3.5',
    identifierClassName,
    initiallyExpanded = false,
    itemId,
    forceExpanded,
    ...rest
}) => {
    const [addKeydownClass, setAddKeydownClass] = useState(false);
    const { open, ExpandableContainer, toggleOpen } = useExpandableContainer({ initiallyExpanded, itemId });
    const { inOfflineMode } = useOfflineModeManager();
    const expandedContent = useMemo(() => hasExpandedContent(rest) && rest.expandedContent, [rest]);
    const onPress = useMemo(() => hasOnPress(rest) && rest.onPress, [rest]);

    const onClick = useCallback(() => {
        if (expandedContent) toggleOpen();
        else if (onPress) onPress();
    }, [expandedContent, onPress, toggleOpen]);

    const handleKeyPress = useCallback(
        (type: 'down' | 'up') => (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            if (type === 'down') {
                setAddKeydownClass(true);
                onClick();
                return;
            }
            setAddKeydownClass(false);
        },
        [onClick],
    );

    useEffect(() => {
        if (forceExpanded) {
            toggleOpen(true);
        }
    }, [forceExpanded, toggleOpen]);

    if (!title) return null;

    return (
        <div
            className={getClassName({
                endIcon,
                onPress,
                addKeydownClass,
                fullWidth,
                locked: !availableOffline && inOfflineMode,
                identifierClassName,
            })}
        >
            <ExpandableContainer
                onClick={onClick}
                className={twMerge(
                    'flex items-center gap-2',
                    getPaddingClass(padding),
                    getHeightClass(height, desktopHeight),
                )}
                onKeyDown={handleKeyPress('down')}
                onKeyUp={handleKeyPress('up')}
            >
                <ContentItemIcon
                    src={src}
                    availableOffline={availableOffline}
                    alt={title}
                    size={imageSize}
                    desktopSize={desktopImageSize}
                    cacheForever
                />
                <ContentItemBoxText
                    title={title}
                    titleSize={titleSize}
                    subtitle={subtitle}
                    showCloud={availableOffline}
                />
                <ContentItemBoxEndIcon expandedContent={expandedContent} endIcon={endIcon} open={open} />
            </ExpandableContainer>
            <ContentItemBoxExpandableContainer expandedContent={expandedContent} open={open} />
        </div>
    );
};
